/**
 * useGlobalEntity — canonical hook for the platform-wide legal entity scope.
 *
 * This is the SINGLE SOURCE OF TRUTH for which legal entity the user is
 * working in. Every view that displays per-entity data should call this hook
 * and pass `entity` to its API requests via `withEntityParam()`.
 *
 * Pattern (in any view):
 *
 *     import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
 *
 *     const { entity } = useGlobalEntity();
 *     const load = useCallback(async () => {
 *       const res = await authFetch(withEntityParam(`${API}/summary`, entity));
 *       ...
 *     }, [entity]);  // re-fetches when global entity changes
 *
 *     useEffect(() => { load(); }, [load]);
 *
 * Semantics:
 *   - entity === ''   → "All Entities" / consolidated view (no filter)
 *   - entity === '*'  → same as ''
 *   - entity === 'XX' → filter to that specific entity_code
 *
 * Backend services interpret an absent or empty entity_code the same way:
 * no filter applied. So views can safely call withEntityParam() always —
 * if entity is empty the URL is unchanged.
 */
import { useApp } from '../contexts/AppContext';

export interface GlobalEntityState {
  entity: string;
  setEntity: (e: string) => void;
  isAllEntities: boolean;
}

export function useGlobalEntity(): GlobalEntityState {
  const { entity, setEntity } = useApp();
  return {
    entity,
    setEntity,
    isAllEntities: !entity || entity === '*',
  };
}

/**
 * Append entity_code as a query parameter to a URL — but only if a specific
 * entity is selected. Empty / '*' / null returns the URL unchanged so the
 * backend treats it as a consolidated request.
 */
export function withEntityParam(url: string, entity: string | null | undefined): string {
  if (!entity || entity === '*') return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}entity_code=${encodeURIComponent(entity)}`;
}
